import { useEffect, useRef, useState } from 'react';
import { Stage, Layer as CanvasLayer } from 'react-konva';
import Konva from 'konva';
import { CANVAS_SIZE, normalizeLayerTransform, type CardEdit, type Layer } from '@str/shared';
import { loadImage, sourceUrl } from '@/lib/composer-cards';
import { pictureNodes, PICTURE_FONTS } from '@/lib/picture-render';
export type PictureTool = 'Crop' | 'Text' | 'Adjust' | 'Sticker' | 'Draw';
type Props = {
  edit: CardEdit;
  onChange: (edit: CardEdit) => void;
  tool: PictureTool;
  selected: string | null;
  onSelect: (id: string | null) => void;
  editing: string | null;
  onEditing: (id: string | null) => void;
  color: string;
  strokeWidth: number;
  disabled: boolean;
  finishTextOnBlur?: boolean;
};
export function PictureStage({
  edit,
  onChange,
  tool,
  selected,
  onSelect,
  editing,
  onEditing,
  color,
  strokeWidth,
  disabled,
  finishTextOnBlur = false,
}: Props) {
  const host = useRef<HTMLDivElement>(null);
  const layerRef = useRef<Konva.Layer>(null);
  const [width, setWidth] = useState(300);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState('');
  const drawing = useRef<string | null>(null);
  const lastTap = useRef<{ id: string; at: number } | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const size = CANVAS_SIZE[edit.canvas];
  const height = (width * size.height) / size.width;
  const url = sourceUrl(edit.source);
  useEffect(() => {
    let active = true;
    Promise.all([loadImage(url), document.fonts?.ready])
      .then(([image]) => {
        if (active) {
          setImage(image);
          setError('');
        }
      })
      .catch(() => {
        if (active) setError('Could not load this picture. Return to Crop to try again.');
      });
    return () => {
      active = false;
    };
  }, [url]);
  useEffect(() => {
    const node = host.current;
    if (!node) return;
    const measure = () =>
      setWidth(
        Math.max(
          1,
          Math.min(540, node.clientWidth, (node.clientHeight * size.width) / size.height),
        ),
      );
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    measure();
    return () => observer.disconnect();
  }, [size]);
  useEffect(() => {
    if (editing) {
      textarea.current?.focus();
      textarea.current?.setSelectionRange(300, 300);
    }
  }, [editing]);
  useEffect(() => {
    const canvas = layerRef.current;
    if (!canvas || !image) return;
    canvas.destroyChildren();
    const nodes = pictureNodes(edit, image, width);
    canvas.add(...nodes);
    for (const item of nodes.slice(1)) {
      const node: Konva.Node = item;
      const stored = edit.layers.find((l) => l.id === node.id())!;
      node.on('click tap', () => {
        if (!disabled && tool !== 'Draw') {
          const now = Date.now();
          const twice = lastTap.current?.id === stored.id && now - lastTap.current.at < 450;
          lastTap.current = { id: stored.id, at: now };
          onSelect(node.id());
          if ((tool === 'Text' || twice) && stored.kind === 'text') onEditing(node.id());
        }
      });
      if (stored.kind === 'path') continue;
      node.draggable(!disabled && tool !== 'Draw' && editing !== stored.id);
      const update = () => {
        const text = (node as Konva.Group).findOne('Text') as Konva.Text;
        const change = normalizeLayerTransform(
          {
            x: node.x(),
            y: node.y(),
            width: text.width() * node.scaleX(),
            size: text.fontSize() * node.scaleY(),
            rotation: node.rotation(),
          },
          { width, height },
          stored.kind,
        );
        onChange({
          ...edit,
          layers: edit.layers.map((l) => (l.id === stored.id ? { ...l, ...change } : l)),
        });
      };
      node.on('dragend transformend', update);
      node.on('dblclick dbltap', () => {
        if (stored.kind === 'text' && !disabled) {
          onSelect(stored.id);
          onEditing(stored.id);
        }
      });
      if (selected === stored.id && editing !== stored.id && tool !== 'Draw') {
        const transformer = new Konva.Transformer({
          nodes: [node],
          flipEnabled: false,
          enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
          keepRatio: true,
          boundBoxFunc: (old, next) => (next.width < 8 || next.height < 8 ? old : next),
        });
        canvas.add(transformer);
      }
      if (editing === stored.id) node.visible(false);
    }
    canvas.draw();
  }, [
    edit,
    image,
    width,
    height,
    selected,
    editing,
    tool,
    disabled,
    onChange,
    onSelect,
    onEditing,
  ]);
  const point = (stage: Konva.Stage) => {
    const p = stage.getPointerPosition();
    return p
      ? { x: Math.max(0, Math.min(1, p.x / width)), y: Math.max(0, Math.min(1, p.y / height)) }
      : null;
  };
  const text = edit.layers.find((l) => l.id === editing && l.kind === 'text');
  return (
    <div className="picture-stage" ref={host} aria-label="Picture canvas">
      {error && <span role="alert">{error}</span>}
      <div style={{ position: 'relative', width, height }}>
        <Stage
          width={width}
          height={height}
          onMouseDown={start}
          onTouchStart={start}
          onMouseMove={move}
          onTouchMove={move}
          onMouseUp={() => {
            drawing.current = null;
          }}
          onTouchEnd={() => {
            drawing.current = null;
          }}
        >
          <CanvasLayer ref={layerRef} />
        </Stage>
        {text?.kind === 'text' && (
          <textarea
            ref={textarea}
            aria-label="Text on picture"
            onBlur={() => {
              if (finishTextOnBlur) onEditing(null);
            }}
            maxLength={300}
            value={text.text}
            disabled={disabled}
            style={{
              position: 'absolute',
              left: text.x * width,
              top: text.y * height,
              width: text.w * width,
              minHeight: text.size * width * 2,
              fontSize: text.size * width,
              fontFamily: PICTURE_FONTS[text.font],
              color: text.color,
              background: text.pill ?? 'transparent',
              textAlign: text.align,
              transform: `rotate(${text.rotate}deg)`,
              transformOrigin: 'top left',
              lineHeight: 1.05,
              padding: 0,
              border: '1px dashed #fff',
              resize: 'none',
              textShadow: '0 2px 20px #0006',
            }}
            onChange={(e) =>
              onChange({
                ...edit,
                layers: edit.layers.map((l) =>
                  l.id === text.id ? { ...text, text: e.target.value } : l,
                ),
              })
            }
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                onEditing(null);
              }
            }}
          />
        )}
      </div>
    </div>
  );
  function start(e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) {
    if (disabled) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const p = point(stage);
    if (!p) return;
    if (tool === 'Draw') {
      if (edit.layers.length >= 50) return;
      e.evt.preventDefault();
      onEditing(null);
      const id = crypto.randomUUID();
      drawing.current = id;
      const path: Layer = {
        id,
        kind: 'path',
        points: [p.x, p.y, p.x, p.y],
        color,
        width: strokeWidth,
      };
      onSelect(id);
      onChange({ ...edit, layers: [...edit.layers, path] });
    } else if (e.target === stage || e.target.name() === 'photo') {
      onEditing(null);
      onSelect(null);
      if (tool === 'Text' && edit.layers.length < 50) {
        const id = crypto.randomUUID();
        onChange({
          ...edit,
          layers: [
            ...edit.layers,
            {
              id,
              kind: 'text',
              text: '',
              x: Math.min(0.8, p.x),
              y: p.y,
              w: Math.min(0.8, 1 - Math.min(0.8, p.x)),
              size: 0.07,
              font: 'sans',
              color,
              align: 'left',
              pill: null,
              shade: false,
              rotate: 0,
            },
          ],
        });
        onSelect(id);
        onEditing(id);
      }
    }
  }
  function move(e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) {
    if (!drawing.current || disabled) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const p = point(stage);
    if (!p) return;
    e.evt.preventDefault();
    onChange({
      ...edit,
      layers: edit.layers.map((l) =>
        l.id === drawing.current && l.kind === 'path' && l.points.length <= 3998
          ? { ...l, points: [...l.points, p.x, p.y] }
          : l,
      ),
    });
  }
}
